import { Button, GridLegacy as Grid } from "@mui/material";
import WarningBox from "components/Common/WarningBox/WarningBox";
import ExportButtons from "components/ExportFile/ExportButtons";
import Snackbar from "components/Common/SnackBar/SnackBar.tsx";
import {
	ConfirmationNumberText,
	ExportConfirmationText,
	GeneratingPDFText,
	ImageUploadError,
	ImageUploadErrorMessage,
	PDFDownloadText,
	PDFErrorText,
	ReplacementConfirmationHeader,
	ReplacementConfirmationSubheader,
	ReturnToOriginalOrderText
} from "constants/text";
import useHandlePopState from "hooks/useHandlePopState";
import useReplacementConfirmationPDFGenerator from "hooks/useReplacementConfirmationPDFGenerator";
import useSnackbarEffect from "hooks/useSnackbarEffect";
import styles from "pages/replacements-page-styles.module.css";
import { useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { RootState } from "stores/application.store";
import { models } from "types/api/viewModels.ts";
import { ThanksHeader } from "./ReplacementConfirmationThanks.styles";
import { getOrderAccountInfo } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import SupportCTA from "components/Common/SupportCTA/SupportCTA";
import { getSupportFormUrl } from "utils/supportForm";

export interface ReplacementConfirmationThanksProps {
	orderDetail: models["OrderDetailViewModel"] | null | undefined;
	accounts: models["CustomerAccountViewModel"] | undefined;
}

const ReplacementConfirmationThanks = ({ orderDetail, accounts }: ReplacementConfirmationThanksProps) => {
	const orderAccountInfo = getOrderAccountInfo(orderDetail);
	const cartItems = useSelector((state: RootState) => state.cart.replacements);
	const hasErrorWithImages = useSelector((state: RootState) => state.cart.hasErrorUploadingImages);
	const replacementOrder = useSelector((state: RootState) => state.submittingReplacementOrder.order);
	const todayDate = useRef(new Date());
	const supportFormUrl = getSupportFormUrl({
		type: "Order Help",
		orderNumber: orderDetail?.orderNumber,
		brand: orderDetail?.configurations?.[0]?.globals?.brand?.description,
		poNumber: orderDetail?.poNumber
	});

	const navigateTo = useNavigate();
	const returnToOrderDetail = () => {
		navigateTo(`/details/${orderDetail?.orderId}`);
	};

	const { pdfData, generateReplacementConfirmationPDF } = useReplacementConfirmationPDFGenerator({
		orderDetail,
		cartItems: cartItems,
		replacementOrder: replacementOrder,
		userAccountInformation: orderAccountInfo?.accountDisplay,
		todayDate: todayDate,
		accounts: accounts
	});

	const { snackbarOpen } = useSnackbarEffect(pdfData.loading);

	useHandlePopState({
		title: "Replacement Confirmation",
		path: `/replacements/${orderDetail?.orderId}`
	});

	return (
		<div className={styles.replacementThanks}>
			<Grid
				xs={12}
				className={styles.replacementThanksContent}
			>
				<ThanksHeader>
					<h5 data-testid="replacement-confirmation-thank-you-header">{ReplacementConfirmationHeader}</h5>
					<div style={{ display: "flex", gap: 8 }}>
						<SupportCTA
							href={supportFormUrl}
							data-testid="replacement-confirmation-support-button"
							sx={{ height: 30 }}
						/>
						<ExportButtons
							largePDF={false}
							buttonHeader={ExportConfirmationText}
							generatePDF={generateReplacementConfirmationPDF}
							isOnlyPDF
						/>
					</div>
					<Snackbar
						open={snackbarOpen}
						isLoading={pdfData.loading}
						loadingText={GeneratingPDFText}
						successText={PDFDownloadText}
						errorText={PDFErrorText}
						isError={Boolean(pdfData.error)}
					/>
				</ThanksHeader>
				<p
					data-testid="replacement-confirmation-thank-you-subheader"
					className="body1"
				>
					{ReplacementConfirmationSubheader}
					{replacementOrder?.confirmationNumber && (
						<>
							<br />
							<span data-testid="replacement-confirmation-number">
								{ConfirmationNumberText}: {replacementOrder.confirmationNumber}
							</span>
						</>
					)}
				</p>
			</Grid>
			{hasErrorWithImages && (
				<div
					style={{
						margin: "12px 0 22px"
					}}
				>
					<WarningBox
						warning={ImageUploadError}
						message={ImageUploadErrorMessage}
					/>
				</div>
			)}
			<Button
				variant="contained"
				onClick={returnToOrderDetail}
				data-testid="replacement-confirmation-details-button"
				data-id="replacement-confirmation-details-button"
			>
				{ReturnToOriginalOrderText}
			</Button>
		</div>
	);
};

export default ReplacementConfirmationThanks;
